import { DOCUMENT } from '@angular/common';
import { computed, effect, Inject, Injectable, signal } from '@angular/core';
import { APP_THEMES, appThemeById, DEFAULT_APP_THEME_ID, type AppTheme } from './app-theme-palettes';
import {
  applyCssVars,
  buildIonicColorCssVars,
  buildOverlaySurfaceStepCssVars,
  commaRgbFromHex,
  formControlTextColor,
  overlayActionTextColor,
  pageContrastTextColor,
  pageErrorTextColor,
  type IonicThemeColorName,
} from './ionic-color.util';
import { buildAccordionTileThemeVars } from './accordion-tile-theme.util';
import { ROBLOX_PAGE_BACKGROUND_PALETTE } from './page-background-palette';
import { LocalStorageService } from '../storage/local-storage.service';
import { LOCAL_STORAGE_KEYS } from '../storage/storage-keys';

const IONIC_THEME_COLOR_NAMES: readonly IonicThemeColorName[] = [
  'primary',
  'secondary',
  'tertiary',
  'success',
  'warning',
  'danger',
  'light',
  'medium',
  'dark',
];

/**
 * Persists the selected app theme and publishes Ionic CSS variables on `:root`.
 * Sets page background, text contrast, and full `--ion-color-*` sets per theme.
 */
@Injectable({ providedIn: 'root' })
export class PageBackgroundPreference {
  /** @deprecated Use {@link APP_THEMES} — kept for Settings swatch grid metadata. */
  readonly palette = ROBLOX_PAGE_BACKGROUND_PALETTE;

  readonly themes = APP_THEMES;

  private readonly storageKey = LOCAL_STORAGE_KEYS.pageBackgroundId;

  private readonly selectedId = signal<string>(DEFAULT_APP_THEME_ID);

  readonly selectedTheme = computed(
    (): AppTheme => appThemeById(this.selectedId()),
  );

  constructor(
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly localStorageService: LocalStorageService,
  ) {
    this.selectedId.set(this.readInitialId());
    this.applyTheme(this.selectedTheme());
    effect(() => {
      this.applyTheme(this.selectedTheme());
    });
  }

  selectionId(): string {
    return this.selectedId();
  }

  selectById(id: string): void {
    if (APP_THEMES.some((t) => t.id === id)) {
      this.selectedId.set(id);
    }
  }

  private readInitialId(): string {
    const raw = this.localStorageService.getItem(this.storageKey);
    if (typeof raw === 'string' && APP_THEMES.some((t) => t.id === raw)) {
      return raw;
    }
    return DEFAULT_APP_THEME_ID;
  }

  private applyTheme(theme: AppTheme): void {
    const root = this.doc.documentElement;
    const bg = theme.pageBackground;
    const fg = pageContrastTextColor(bg);
    const err = pageErrorTextColor(bg);
    const formText = formControlTextColor(bg, fg);

    const overlayBg = theme.colors.light;
    const overlayFg = pageContrastTextColor(overlayBg);

    const vars: Record<string, string> = {
      '--app-page-background': bg,
      '--app-page-background-contrast': fg,
      '--app-page-background-contrast-rgb': commaRgbFromHex(fg),
      '--app-page-error': err,
      '--app-form-control-text': formText,
      '--app-form-control-text-rgb': commaRgbFromHex(formText),
      ...buildAccordionTileThemeVars(theme.colors, bg),
      '--app-overlay-background': overlayBg,
      '--app-overlay-background-rgb': commaRgbFromHex(overlayBg),
      '--app-overlay-color': overlayFg,
      '--app-overlay-color-rgb': commaRgbFromHex(overlayFg),
      '--app-overlay-action': overlayActionTextColor(
        theme.colors.primary,
        overlayBg,
      ),
      '--app-overlay-destructive': pageErrorTextColor(overlayBg),
      ...buildOverlaySurfaceStepCssVars(overlayBg, overlayFg),
      '--ion-background-color': bg,
      '--ion-text-color': fg,
      '--ion-text-color-rgb': commaRgbFromHex(fg),
    };

    for (const name of IONIC_THEME_COLOR_NAMES) {
      Object.assign(vars, buildIonicColorCssVars(name, theme.colors[name]));
    }

    applyCssVars(root, vars);
    this.localStorageService.setItem(this.storageKey, theme.id);

    const meta = this.doc.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', bg);
    }
  }
}
